<div class="sub-mgmt">

    <!-- PAGE HEADER -->
    <div class="page-header">
        <div class="header-text">
            <h1 class="page-title">Subscription Plan Management</h1>
            <p class="page-subtitle">Configure marketplace subscription plans, listing rules, booking limits, and vendor
                access.</p>
        </div>
        <div class="header-actions">
            <button class="btn btn-ghost" (click)="openOfferModal()">
                <mat-icon>campaign</mat-icon>
                Offer Banner
            </button>
            <button class="btn btn-primary" (click)="openCreateModal()">
                <mat-icon>add</mat-icon>
                Create Plan
            </button>
        </div>
    </div>

    <!-- STATS -->
    <div class="stats-row">
        <div class="stat-card">
            <div class="stat-header">
                <div class="stat-label">Monthly Recurring Revenue</div>
                <mat-icon class="stat-icon stat-icon--brand">payments</mat-icon>
            </div>
            <div class="stat-value">AED {{ planStats.mrr | number:'1.0-0' }}</div>
            <div class="stat-sub">Platform fees only · listing fees billed per event</div>
        </div>

        <div class="stat-card">
            <div class="stat-header">
                <div class="stat-label">Total Vendors</div>
                <mat-icon class="stat-icon stat-icon--brand">storefront</mat-icon>
            </div>
            <div class="stat-value">{{ planStats.totalVendors | number }}</div>
            <div class="stat-split">
                <span class="split-item split-item--paid"><span class="split-dot split-dot--paid"></span>{{
                    planStats.paidVendors }} paid</span>
                <span class="split-item split-item--free"><span class="split-dot split-dot--free"></span>{{
                    planStats.freeVendors }} free</span>
            </div>
        </div>

        <div class="stat-card" [class.stat-card--warn]="planStats.vendorsNearLimit > 0">
            <div class="stat-header">
                <div class="stat-label">Near Booking Limit</div>
                <mat-icon class="stat-icon" [class.stat-icon--warn]="planStats.vendorsNearLimit > 0"
                    [class.stat-icon--muted]="planStats.vendorsNearLimit === 0">trending_up</mat-icon>
            </div>
            <div class="stat-value">{{ planStats.vendorsNearLimit }}</div>
            <div class="stat-sub">&gt;80% of quota used · upsell opportunity</div>
        </div>

        <div class="stat-card" [class.stat-card--danger]="planStats.gracePeriodAccounts > 0">
            <div class="stat-header">
                <div class="stat-label">Grace Period Accounts</div>
                <mat-icon class="stat-icon" [class.stat-icon--danger]="planStats.gracePeriodAccounts > 0"
                    [class.stat-icon--muted]="planStats.gracePeriodAccounts === 0">warning_amber</mat-icon>
            </div>
            <div class="stat-value">{{ planStats.gracePeriodAccounts }}</div>
            <div class="stat-sub">Payment failed · at risk of downgrade</div>
        </div>
    </div>

    <!-- TABLE -->
    <div class="table-section">
        <div class="table-toolbar">
            <div class="toolbar-search">
                <mat-icon>search</mat-icon>
                <input type="text" placeholder="Search plans…" [(ngModel)]="searchTerm" (input)="filterPlans()" />
            </div>
            <div class="toolbar-filters">
                <select class="filter-select" [(ngModel)]="statusFilter" (change)="filterPlans()">
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                </select>
                <select class="filter-select" [(ngModel)]="sortField" (change)="sortPlans()">
                    <option value="name">Name</option>
                    <option value="monthlyFee">Monthly Fee</option>
                    <option value="listingFee">Listing Fee</option>
                    <option value="bookingLimit">Booking Limit</option>
                    <option value="subscriberCount">Subscribers</option>
                </select>
            </div>
        </div>

        <div class="table-wrap">
            <table class="admin-table">
                <thead>
                    <tr>
                        <th>Plan</th>
                        <th>Monthly Fee</th>
                        <th>Listing Fee</th>
                        <th>Active Listings</th>
                        <th>Booking Limit</th>
                        <th>Trial</th>
                        <th>Marketplace</th>
                        <th>Subscribers</th>
                        <th>Status</th>
                        <th>Last Updated</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let plan of paginatedPlans" [class.row-disabled]="!plan.isActive">
                        <td>
                            <div class="plan-cell">
                                <span class="plan-dot" [style.background]="plan.color"></span>
                                <div>
                                    <div class="plan-name">{{ plan.name }}</div>
                                    <div class="plan-tagline" *ngIf="plan.tagline">{{ plan.tagline }}</div>
                                </div>
                            </div>
                        </td>
                        <td><span class="mono-val">{{ plan.monthlyFee === 0 ? 'Free' : 'AED ' + plan.monthlyFee
                                }}</span></td>
                        <td>
                            <span *ngIf="plan.marketplaceEnabled" class="mono-val">{{ plan.listingFee === 0 ? '—' : 'AED
                                ' + plan.listingFee }}</span>
                            <span *ngIf="!plan.marketplaceEnabled" class="val-na">N/A</span>
                        </td>
                        <td>
                            <span class="limit-pill" [class.limit-pill--unlimited]="plan.activeListingLimit === 0">
                                {{ plan.activeListingLimit === 0 ? 'Unlimited' : plan.activeListingLimit }}
                            </span>
                        </td>
                        <td>
                            <span class="limit-pill" [class.limit-pill--unlimited]="plan.bookingLimit === 0">
                                {{ plan.bookingLimit === 0 ? 'Unlimited' : plan.bookingLimit }}
                            </span>
                        </td>
                        <td>
                            <span *ngIf="plan.trialEnabled" class="tag tag--amber">{{ plan.trialDays }}d trial</span>
                            <span *ngIf="!plan.trialEnabled" class="val-na">None</span>
                        </td>
                        <td>
                            <span class="tag" [class.tag--green]="plan.marketplaceEnabled"
                                [class.tag--red]="!plan.marketplaceEnabled">
                                {{ plan.marketplaceEnabled ? 'Enabled' : 'Disabled' }}
                            </span>
                        </td>
                        <td class="mono-val">{{ plan.subscriberCount }}</td>
                        <td>
                            <span class="status-chip" [class.chip--active]="plan.isActive"
                                [class.chip--disabled]="!plan.isActive">
                                <span class="chip-dot"></span>{{ plan.isActive ? 'Active' : 'Disabled' }}
                            </span>
                        </td>
                        <td class="date-cell">{{ plan.lastUpdated | date:'d MMM y' }}</td>
                        <td>
                            <div class="action-wrap" [matMenuTriggerFor]="actionMenu">
                                <button class="icon-btn"><mat-icon>more_vert</mat-icon></button>
                                <mat-menu #actionMenu="matMenu">
                                    <button mat-menu-item (click)="openEditModal(plan)"><mat-icon>edit</mat-icon>
                                        Edit</button>
                                    <button mat-menu-item
                                        (click)="duplicatePlan(plan)"><mat-icon>content_copy</mat-icon> Clone</button>
                                    <button mat-menu-item (click)="togglePlanStatus(plan)">
                                        <mat-icon>{{ plan.isActive ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                                        {{ plan.isActive ? 'Disable' : 'Enable' }}
                                    </button>
                                    <mat-divider></mat-divider>
                                    <button mat-menu-item class="danger-item"
                                        (click)="deletePlan(plan.id)"><mat-icon>delete_outline</mat-icon>
                                        Delete</button>
                                </mat-menu>
                            </div>
                        </td>
                    </tr>
                    <tr *ngIf="paginatedPlans.length === 0">
                        <td colspan="11">
                            <div class="empty-state">
                                <mat-icon>inbox</mat-icon>
                                <p>No plans match your filters.</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="pagination">
            <span class="page-info">Showing {{ pageStart }}–{{ pageEnd }} of {{ filteredPlans.length }} plans</span>
            <div class="page-btns">
                <button class="page-btn" [disabled]="currentPage === 1"
                    (click)="prevPage()"><mat-icon>chevron_left</mat-icon></button>
                <button class="page-btn page-btn--num" *ngFor="let p of totalPagesArray"
                    [class.active]="p === currentPage" (click)="goToPage(p)">{{ p }}</button>
                <button class="page-btn" [disabled]="currentPage === totalPages"
                    (click)="nextPage()"><mat-icon>chevron_right</mat-icon></button>
            </div>
        </div>
    </div>

    <!-- WORKER MANAGEMENT SETTINGS SECTION -->
    <div class="table-section" style="margin-top: 24px;">
        <div class="table-toolbar" style="padding: 16px 24px; border-bottom: 1px solid #f1f5f9;">
            <div style="display: flex; align-items: center; gap: 8px;">
                <mat-icon style="color: #6366f1;">people</mat-icon>
                <h2 style="margin: 0; font-size: 18px; font-weight: 600; color: #1e293b;">Worker Management</h2>
            </div>
        </div>

        <div style="padding: 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; background: white; border-radius: 0 0 12px 12px;">
            <!-- Left Config Form -->
            <div style="display: flex; flex-direction: column; gap: 20px;">
                <!-- Toggle -->
                <div class="toggle-row" style="margin: 0; padding: 0; border: none; display: flex; justify-content: space-between; align-items: center;">
                    <div class="toggle-info">
                        <span class="toggle-label" style="font-weight: 600; color: #334155;">Enable Worker Management</span>
                        <span class="toggle-desc" style="display: block; font-size: 12px; color: #64748b; margin-top: 4px;">Allow vendors to invite workers to their workspace</span>
                    </div>
                    <label class="switch">
                        <input type="checkbox" [(ngModel)]="workerSettings.enabled" (change)="saveWorkerSettings()" />
                        <span class="switch-track"></span>
                    </label>
                </div>

                <!-- Limit Number Input -->
                <div class="form-group" style="margin: 0;">
                    <label style="display: block; font-weight: 600; color: #334155; margin-bottom: 8px;">Maximum Workers Per Vendor</label>
                    <input type="number" class="form-control" [(ngModel)]="workerSettings.defaultWorkerLimit" min="1" (change)="saveWorkerSettings()" style="max-width: 200px;" />
                </div>
            </div>

            <!-- Right Read-only Statistics -->
            <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; display: flex; flex-direction: column; gap: 16px;">
                <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600; color: #475569; text-transform: uppercase; letter-spacing: 0.05em;">Read-only Statistics</h3>
                
                <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e2e8f0; padding-bottom: 12px;">
                    <span style="font-size: 13px; color: #475569; font-weight: 500;">Worker Accounts</span>
                    <strong style="font-size: 18px; color: #1e293b;">{{ workerStats.totalWorkers }}</strong>
                </div>

                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="font-size: 13px; color: #475569; font-weight: 500;">Vendors Using Workers</span>
                    <strong style="font-size: 18px; color: #1e293b;">{{ workerStats.vendorsCount }}</strong>
                </div>
            </div>
        </div>
    </div>

    <!-- ══════════════════════════════════════════
       OFFER BANNER MODAL
  ══════════════════════════════════════════ -->
    <div class="modal-backdrop" *ngIf="showOfferModal" (click)="closeOfferModal()">
        <div class="modal-panel" (click)="$event.stopPropagation()">

            <div class="modal-header">
                <div style="display:flex;align-items:center;gap:8px">
                    <mat-icon style="color:#6366f1;font-size:18px;width:18px;height:18px">campaign</mat-icon>
                    <h3>Platform-Wide Offer Banner</h3>
                </div>
                <button class="modal-close" (click)="closeOfferModal()"><mat-icon>close</mat-icon></button>
            </div>

            <div class="modal-body">

                <div class="modal-section">
                    <div class="toggle-row">
                        <div class="toggle-info">
                            <span class="toggle-label">Banner Active</span>
                            <span class="toggle-desc">Show this offer to all vendors on the subscription page</span>
                        </div>
                        <label class="switch">
                            <input type="checkbox" [(ngModel)]="platformOffer.enabled" />
                            <span class="switch-track"></span>
                        </label>
                    </div>
                </div>

                <div class="modal-section" *ngIf="platformOffer.enabled">
                    <div class="modal-section-header"><mat-icon>visibility</mat-icon><span>Preview</span></div>
                    <div class="offer-preview-strip">
                        <div class="offer-preview-inner">
                            <div class="offer-preview-badge">
                                <mat-icon style="font-size:13px;width:13px;height:13px">card_giftcard</mat-icon>
                                {{ platformOffer.badgeText || 'Limited Offer' }}
                            </div>
                            <div class="offer-preview-text">
                                <strong>{{ platformOffer.highlightText || '3 months free' }}</strong>
                                {{ platformOffer.bodyText || 'on any paid plan' }}
                            </div>
                            <div class="offer-preview-countdown">
                                <mat-icon style="font-size:12px;width:12px;height:12px">schedule</mat-icon>
                                {{ platformOffer.countdownLabel || 'Introductory pricing · ends soon' }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>edit_note</mat-icon><span>Content</span></div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Badge Text</label>
                            <input type="text" class="form-control" [(ngModel)]="platformOffer.badgeText"
                                placeholder="e.g. Limited Offer" />
                        </div>
                        <div class="form-group">
                            <label>Highlight Text <small>(bold)</small></label>
                            <input type="text" class="form-control" [(ngModel)]="platformOffer.highlightText"
                                placeholder="e.g. 3 months free" />
                        </div>
                        <div class="form-group">
                            <label>Body Text</label>
                            <input type="text" class="form-control" [(ngModel)]="platformOffer.bodyText"
                                placeholder="e.g. on any paid plan" />
                        </div>
                        <div class="form-group">
                            <label>Countdown / Sub-label</label>
                            <input type="text" class="form-control" [(ngModel)]="platformOffer.countdownLabel"
                                placeholder="e.g. Introductory pricing · ends soon" />
                        </div>
                        <div class="form-group">
                            <label>Expiry Date <small>(optional)</small></label>
                            <input type="date" class="form-control" [(ngModel)]="platformOffer.expiryDate" />
                        </div>
                    </div>
                </div>

            </div>

            <div class="modal-footer">
                <button class="btn btn-ghost btn-sm" (click)="resetOffer()">Reset defaults</button>
                <button class="btn btn-ghost" (click)="closeOfferModal()">Cancel</button>
                <button class="btn btn-primary" (click)="saveOffer()" [disabled]="offerSaving">
                    <mat-icon *ngIf="offerSaving" class="spin">autorenew</mat-icon>
                    <mat-icon *ngIf="!offerSaving">save</mat-icon>
                    {{ offerSaving ? 'Saving…' : 'Save Offer' }}
                </button>
            </div>

        </div>
    </div>


    <!-- ══════════════════════════════════════════
       CREATE / EDIT PLAN MODAL
  ══════════════════════════════════════════ -->
    <div class="modal-backdrop" *ngIf="showModal" (click)="closeModal()">
        <div class="modal-panel" (click)="$event.stopPropagation()">

            <div class="modal-header">
                <h3>{{ isEditing ? 'Edit Plan' : 'Create New Plan' }}</h3>
                <button class="modal-close" (click)="closeModal()"><mat-icon>close</mat-icon></button>
            </div>

            <div class="modal-body">

                <!-- ── Basic Information ─────────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>info</mat-icon><span>Basic Information</span></div>
                    <div class="form-grid">
                        <div class="form-group form-group--full">
                            <label>Plan Name <span class="req">*</span></label>
                            <input type="text" class="form-control" [(ngModel)]="modalPlan['name']"
                                placeholder="e.g. Growth" />
                        </div>
                        <div class="form-group form-group--full">
                            <label>Tagline</label>
                            <input type="text" class="form-control" [(ngModel)]="modalPlan['tagline']"
                                placeholder="Short description shown to vendors" />
                        </div>
                        <div class="form-group form-group--full">
                            <label>Plan Color</label>
                            <div class="color-picker">
                                <span *ngFor="let c of colorPresets" class="color-swatch" [style.background]="c"
                                    [class.color-swatch--active]="modalPlan['color'] === c"
                                    (click)="setModalColor(c)"></span>
                                <input type="color" class="color-native" [(ngModel)]="modalPlan['color']" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ── Pricing & Limits ──────────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>payments</mat-icon><span>Pricing &amp; Limits</span>
                    </div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Monthly Fee (AED)</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['monthlyFee']" min="0" />
                        </div>
                        <div class="form-group">
                            <label>Listing Fee (AED)</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['listingFee']" min="0" />
                        </div>
                        <div class="form-group">
                            <label>Active Listing Limit <small>(0 = Unlimited)</small></label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['activeListingLimit']"
                                min="0" />
                        </div>
                        <div class="form-group">
                            <label>Booking Limit <small>(0 = Unlimited)</small></label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['bookingLimit']" min="0" />
                        </div>
                    </div>
                </div>

                <!-- ── Booking Add-ons ───────────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>add_circle_outline</mat-icon><span>Booking
                            Add-ons</span></div>
                    <div class="toggle-row">
                        <div class="toggle-info">
                            <span class="toggle-label">Add-On Booking Packs</span>
                            <span class="toggle-desc">Allow vendors to purchase extra booking slots</span>
                        </div>
                        <label class="switch">
                            <input type="checkbox" [(ngModel)]="modalPlan['addOnBookingEnabled']" />
                            <span class="switch-track"></span>
                        </label>
                    </div>
                    <div class="form-grid" *ngIf="modalPlan['addOnBookingEnabled']">
                        <div class="form-group">
                            <label>Pack Price (AED)</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['addOnBookingPrice']"
                                min="0" />
                        </div>
                        <div class="form-group">
                            <label>Bookings per Pack</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['addOnBookingCount']"
                                min="1" />
                        </div>
                    </div>
                </div>

                <!-- ── Marketplace & Listings ────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>storefront</mat-icon><span>Marketplace &amp;
                            Listings</span></div>

                    <!-- Listed Equipment Module toggle -->
                    <div class="toggle-row">
                        <div class="toggle-info">
                            <span class="toggle-label">Listed Equipment Module</span>
                            <span class="toggle-desc">Enable the Listed Equipment section for vendors on this
                                plan</span>
                        </div>
                        <label class="switch">
                            <input type="checkbox" [(ngModel)]="modalPlan['listedEquipmentModuleEnabled']" />
                            <span class="switch-track"></span>
                        </label>
                    </div>

                    <!-- Listed Equipment Cap (only shown when listedEquipmentModuleEnabled is true) -->
                    <div class="form-grid" *ngIf="modalPlan['listedEquipmentModuleEnabled']" style="margin-top: 12px; margin-bottom: 12px;">
                        <div class="form-group">
                            <label>Listed Equipment Cap <small>(max allowed vehicles/equipment, 0 = Unlimited)</small></label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['listedEquipmentCap']" min="0" placeholder="e.g. 5" />
                        </div>
                    </div>

                    <!-- Marketplace access (only relevant when module is on) -->
                    <div *ngIf="modalPlan['listedEquipmentModuleEnabled']">
                        <div class="toggle-row">
                            <div class="toggle-info">
                                <span class="toggle-label">Marketplace Access</span>
                                <span class="toggle-desc">Vendors can list equipment publicly in the marketplace</span>
                            </div>
                            <label class="switch">
                                <input type="checkbox" [(ngModel)]="modalPlan['marketplaceEnabled']" />
                                <span class="switch-track"></span>
                            </label>
                        </div>

                        <div class="form-grid" *ngIf="modalPlan['marketplaceEnabled']">
                            <div class="form-group">
                                <label>Listing Duration (days)</label>
                                <input type="number" class="form-control" [(ngModel)]="modalPlan['listingDurationDays']"
                                    min="1" />
                            </div>
                        </div>

                        <!-- Expiry reminder (shown when marketplace is on) -->
                        <div class="toggle-row" *ngIf="modalPlan['marketplaceEnabled']">
                            <div class="toggle-info">
                                <span class="toggle-label">Listing Expiry Reminder</span>
                                <span class="toggle-desc">Notify vendors before a listing expires</span>
                            </div>
                            <label class="switch">
                                <input type="checkbox" [(ngModel)]="modalPlan['listingExpiryReminderEnabled']" />
                                <span class="switch-track"></span>
                            </label>
                        </div>
                        <div class="form-grid"
                            *ngIf="modalPlan['marketplaceEnabled'] && modalPlan['listingExpiryReminderEnabled']">
                            <div class="form-group">
                                <label>Reminder Lead Time (days before expiry)</label>
                                <input type="number" class="form-control"
                                    [(ngModel)]="modalPlan['listingExpiryReminderDays']" min="1" />
                            </div>
                        </div>
                    </div>

                    <!-- Booking cycle / grace period — always shown -->
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Booking Cycle (days)</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['manualBookingCycleDays']"
                                min="1" />
                        </div>
                        <div class="form-group">
                            <label>Grace Period (days)</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['gracePeriodDays']"
                                min="0" />
                        </div>
                    </div>
                </div>

                <!-- ── Trial ─────────────────────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>timer</mat-icon><span>Trial</span></div>
                    <div class="toggle-row">
                        <div class="toggle-info">
                            <span class="toggle-label">Trial Enabled</span>
                            <span class="toggle-desc">Offer a free trial period for new subscribers</span>
                        </div>
                        <label class="switch">
                            <input type="checkbox" [(ngModel)]="modalPlan['trialEnabled']" />
                            <span class="switch-track"></span>
                        </label>
                    </div>
                    <div class="form-grid" *ngIf="modalPlan['trialEnabled']">
                        <div class="form-group">
                            <label>Trial Duration (days)</label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['trialDays']" min="1" />
                        </div>
                        <div class="form-group">
                            <label>Trial Vehicle Cap <small>(max listings during trial)</small></label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['trialVehicleCap']"
                                min="1" />
                        </div>
                    </div>
                </div>

                <!-- ── Storage & Documents ───────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>folder</mat-icon><span>Storage &amp; Documents</span>
                    </div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Storage Limit (GB) <small>(0 = Unlimited)</small></label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['storageLimitGb']"
                                min="0" />
                        </div>
                        <div class="form-group">
                            <label>Document Archive (months) <small>(999 = no limit)</small></label>
                            <input type="number" class="form-control" [(ngModel)]="modalPlan['documentArchiveMonths']"
                                min="1" />
                        </div>
                    </div>
                </div>

                <!-- ── Billing Discounts ─────────────────────────────── -->
                <div class="modal-section">
                    <div class="modal-section-header"><mat-icon>local_offer</mat-icon><span>Billing Discounts</span>
                    </div>
                    <p class="discount-hint">Discounts apply to the base monthly price when vendors choose longer
                        billing cycles.</p>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Quarterly Discount <span class="discount-badge">3 months</span></label>
                            <div class="discount-input-wrap">
                                <input type="number" class="form-control" [(ngModel)]="modalPlan['quarterlyDiscount']"
                                    min="0" max="100" placeholder="5" />
                                <span class="discount-unit">%</span>
                            </div>
                            <span class="discount-preview"
                                *ngIf="(modalPlan['monthlyFee'] ?? 0) > 0 && (modalPlan['quarterlyDiscount'] ?? 0) > 0">
                                AED {{ (modalPlan['monthlyFee'] ?? 0) * (1 - (modalPlan['quarterlyDiscount'] ?? 0) /
                                100) | number:'1.0-0' }} / mo
                            </span>
                        </div>
                        <div class="form-group">
                            <label>Half-Yearly Discount <span class="discount-badge">6 months</span></label>
                            <div class="discount-input-wrap">
                                <input type="number" class="form-control" [(ngModel)]="modalPlan['halfYearlyDiscount']"
                                    min="0" max="100" placeholder="10" />
                                <span class="discount-unit">%</span>
                            </div>
                            <span class="discount-preview"
                                *ngIf="(modalPlan['monthlyFee'] ?? 0) > 0 && (modalPlan['halfYearlyDiscount'] ?? 0) > 0">
                                AED {{ (modalPlan['monthlyFee'] ?? 0) * (1 - (modalPlan['halfYearlyDiscount'] ?? 0) /
                                100) | number:'1.0-0' }} / mo
                            </span>
                        </div>
                        <div class="form-group">
                            <label>Yearly Discount <span class="discount-badge">12 months</span></label>
                            <div class="discount-input-wrap">
                                <input type="number" class="form-control" [(ngModel)]="modalPlan['yearlyDiscount']"
                                    min="0" max="100" placeholder="15" />
                                <span class="discount-unit">%</span>
                            </div>
                            <span class="discount-preview"
                                *ngIf="(modalPlan['monthlyFee'] ?? 0) > 0 && (modalPlan['yearlyDiscount'] ?? 0) > 0">
                                AED {{ (modalPlan['monthlyFee'] ?? 0) * (1 - (modalPlan['yearlyDiscount'] ?? 0) / 100) |
                                number:'1.0-0' }} / mo
                            </span>
                        </div>
                    </div>
                </div>

            </div>

            <div class="modal-footer">
                <button class="btn btn-ghost" (click)="closeModal()">Cancel</button>
                <button class="btn btn-primary" (click)="isEditing ? updatePlan() : createPlan()" [disabled]="loading">
                    <mat-icon *ngIf="loading" class="spin">autorenew</mat-icon>
                    {{ isEditing ? 'Save Changes' : 'Create Plan' }}
                </button>
            </div>

        </div>
    </div>

</div>